import * as Rx from "rxjs";

/**
 * Contains data streams for query results and full data loads.
 */
export interface DataProvider {

    /**
     * Gets a stream of query results from the given stream of queries.
     *
     * Implementations must not change the temperature of "requestStream"
     * observable. I.e., if "requestStream" is a cold observable, the
     * result must be a cold observable. If "requestStream" is a hot observable,
     * the result must be a hot observable.
     *
     * @param requestStream
     *     Observable stream of incoming search requests.
     * @param origin
     *     Origin of the search requests.
     * @returns
     *     Observable stream of search results (same temperature as "requestStream").
     */
    search(requestStream: Rx.Observable<DataProviderRequest>, origin?: QueryOrigin): Rx.Observable<DataProviderResponse>;

}

/**
 * Request to supply to a search stream.
 */
export interface DataProviderRequest {

    /**
     * Search term to use when filtering the result set.
     */
    filter?: string;

    /**
     * Identifier of the currently selected value (if any).
     */
    currentValue?: string;

    /**
     * Origin of the request (if relevant).
     *
     * Will be a string representation of QueryOrigin.
     */
    origin?: "Initial" | "Select" | "Grid";

    /**
     * Any additional context to include that will be attached to the search response.
     */
    context?: any;

}

/**
 * Response from a data provider search query.
 */
export interface DataProviderResponse {

    /**
     * A query result containing the complete set of available data.
     */
    data: {
        /**
         * Items that match a particular query. May be a subset of the complete result set.
         */
        items: any;

        /**
         * The total number of results matching a query.
         */
        resultCount: number;

        /**
         * Allow other properties to be attached to "data".
         */
        [key: string]: any;
    };

    /**
     * A string value containing details of a data error or `null` if no error occured.
     */
    error: string;

    /**
     * Any additional context specified in the request that generated this response.
     */
    context?: any;

}

/**
 * The state of a DataProviderResponse.
 */
export enum DataState {

    /**
     * Data is available.
     */
    Ready = 0,

    /**
     * Data is not ready or in transit.
     */
    Busy = 1,

    /**
     * An error occurred when retrieving data.
     */
    Error = 2

}

/**
 * The origin of a data query.
 */
export enum QueryOrigin {

    /**
     * Query is an initial data request.
     */
    Initial = 0,

    /**
     * Query originated from the 'select' dropdown.
     */
    Select = 1,

    /**
     * Query originated from the picklist grid.
     */
    Grid = 2

}

/** Empty DataProviderValue (stored as const to avoid GC pressure). */
export const emptyValue: DataProviderResponse = { data: null, error: null };

/**
 * Function that produces an Observable stream of DataProviderResponses from a DataProviderRequest.
 */
export type DataProviderRequestHandler = (request: DataProviderRequest) => Rx.Observable<DataProviderResponse>;

/**
 * Gets the DataState for a DataProviderResponse object.
 */
export function stateOfResponse(result: DataProviderResponse): DataState {
    if (result) {
        if (result.error) {
            return DataState.Error;
        } else if (result.data) {
            return DataState.Ready;
        }
    }
    return DataState.Busy;
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/data-provider/dataProvider.ts